export type StreamEventType = 'token' | 'done' | 'error';

export interface StreamEvent {
  type: StreamEventType;
  content: string;
  done: boolean;
  quoteId?: string;
  graphVersion?: number;
  chatId?: string;
  conversationId?: string;
}

/** One prior chat turn, sent to the appetite endpoint for follow-up context. */
export interface ConversationTurn {
  role: 'user' | 'assistant';
  content: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared SSE runner — POSTs a JSON body and streams `data: {...}` events.
// Both streamGraphQuery and streamAppetiteQuery reuse this so the SSE parsing,
// abort handling, and error reporting live in one place.
// ─────────────────────────────────────────────────────────────────────────────

function runSSEStream(
  endpoint: string,
  body: Record<string, unknown>,
  onToken: (token: string) => void,
  onDone: (graphVersion: number) => void,
  onError: (message: string) => void
): AbortController {
  const controller = new AbortController();

  (async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_TRUCKING_PORTAL_AI_API_Url}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!res.ok) {
        onError(`API error ${res.status}: ${res.statusText}`);
        return;
      }

      if (!res.body) {
        onError('No response body from server.');
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed.startsWith('data: ')) continue;
            try {
              const event: StreamEvent = JSON.parse(trimmed.slice(6));
              if (event.type === 'token' && event.content) {
                onToken(event.content);
              } else if (event.type === 'done') {
                onDone(event.graphVersion ?? 0);
                return;
              } else if (event.type === 'error') {
                onError(event.content || 'Unknown error from server.');
                return;
              }
            } catch {
              // Malformed SSE line — skip
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      onError(err instanceof Error ? err.message : 'Network error during streaming.');
    }
  })();

  return controller;
}

/**
 * Stream an answer from the carrier knowledge graph via SSE.
 * @returns AbortController — call .abort() to cancel mid-stream.
 */
export function streamGraphQuery(
  quoteId: string,
  question: string,
  conversationId: string,
  onToken: (token: string) => void,
  onDone: (graphVersion: number) => void,
  onError: (message: string) => void
): AbortController {
  return runSSEStream(
    '/ai/graph/query/stream',
    { quoteId, question, conversationId },
    onToken,
    onDone,
    onError
  );
}

/**
 * Stream a carrier-appetite recommendation for a prospect via SSE.
 * The prospect context is derived server-side from `quoteId`; `history` carries
 * prior turns so follow-up questions keep conversation context.
 * @returns AbortController — call .abort() to cancel mid-stream.
 */
export function streamAppetiteQuery(
  quoteId: string,
  question: string,
  conversationId: string,
  history: ConversationTurn[],
  saveAsRecommendation: boolean,
  onToken: (token: string) => void,
  onDone: (graphVersion: number) => void,
  onError: (message: string) => void
): AbortController {
  return runSSEStream(
    '/ai/appetite/recommend/stream',
    { quoteId, question, conversationId, history, saveAsRecommendation },
    onToken,
    onDone,
    onError
  );
}
